import Markdown from "react-markdown";
import { useState } from "react";
import {
  EditOutlined,
  EyeOutlined,
  QuestionCircleFilled,
} from "@ant-design/icons";
import { Button, Checkbox, Input, Tooltip, theme, Radio } from "antd";
import { DashboardV1State, MarkdownCard } from "pages/api/deep-links/types";
import { Flex } from "src/components/Flex";
import { cloneData } from "src/utils/objects";
import { CardEditor } from "./CardEditor";
import { DashboardRuntime } from "./context";

export function MarkdownViewer({
  isLocked,
  cardId,
  json,
  setEditor,
  onRemoveCard,
  setCardData,
  colorTheme,
}: {
  source: DashboardRuntime["source"];
  colorTheme?: string;
  json: Pick<DashboardV1State, "data">;
  cardId: string;
  isLocked: boolean;
  setEditor(contenet: JSX.Element | null): void;
  onRemoveCard(id: string): void;
  setCardData(cardData: DashboardV1State["data"][string]): void;
}) {
  const card = json.data[cardId];
  if (card.type !== "markdown") throw new Error("Expected markdown card");

  const { token } = theme.useToken();
  return (
    <div
      style={{
        height: "100%",
        width: "100%",
        overflow: "auto",
        backgroundColor: card.transparent
          ? "transparent"
          : colorTheme === "alternate"
          ? token.colorBgContainer
          : token.colorBgLayout,
        borderRadius: 10,
      }}
    >
      {isLocked ? (
        <div
          style={{
            paddingTop: 14,
            paddingLeft: 10,
            paddingRight: 10,
            paddingBottom: 0,
          }}
        >
          <Markdown className="markdown" skipHtml={true}>
            {card.markdown}
          </Markdown>
        </div>
      ) : (
        <div
          style={{
            height: "100%",
            display: "flex",
            cursor: "move",
            alignItems: "flex-start",
            justifyContent: "flex-start",
          }}
        >
          <div
            className="markdown"
            style={{ height: "100%", flex: 1, padding: 6 }}
          >
            <Markdown className="markdown" skipHtml={true}>
              {card.markdown}
            </Markdown>
          </div>
          <div
            style={{
              padding: 4,
              gap: 1,
              display: "flex",
              alignItems: "center",
            }}
          >
            <Button
              onMouseDownCapture={() => {
                setEditor(
                  <MarkdownEditor
                    card={card}
                    onClose={() => setEditor(null)}
                    onChange={setCardData}
                  />
                );
              }}
              title="Edit Markdown"
              size="small"
              shape="circle"
              type="primary"
              style={{ borderTopRightRadius: 0, borderBottomRightRadius: 0 }}
            >
              <span style={{ fontWeight: 600 }}>
                <EditOutlined />
              </span>
            </Button>
            <Button
              onMouseDownCapture={() => {
                onRemoveCard(cardId);
              }}
              title="Remove this card"
              size="small"
              shape="circle"
              danger
              type="primary"
              style={{ borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }}
            >
              <span style={{ fontWeight: 600 }}>&times;</span>
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function MarkdownEditor({
  card,
  onClose,
  onChange,
}: {
  card: MarkdownCard;
  onClose(): void;
  onChange(cardData: DashboardV1State["data"][string]): void;
}) {
  const { token } = theme.useToken();
  const [isPreviewing, setPreviewing] = useState(false);
  const [working, updateWorking] = useState(cloneData(card));
  return (
    <CardEditor
      title="Markdown editor"
      onCancel={() => onClose()}
      onOk={() => {
        onChange(working);
        onClose();
      }}
    >
      <>
        <Flex spaceBetween gap={8} style={{ marginBottom: 16 }}>
          <Flex gap={16}>
            <Radio.Group
              value={isPreviewing}
              onChange={(e) => setPreviewing(e.target.value)}
              buttonStyle={"solid"}
              size="small"
            >
              <Radio.Button value={false}>
                <EditOutlined />
              </Radio.Button>
              <Radio.Button value={true}>
                <EyeOutlined />
              </Radio.Button>
            </Radio.Group>
            <Checkbox
              style={{ whiteSpace: "nowrap" }}
              checked={working.transparent}
              onChange={(e) =>
                updateWorking({ ...working, transparent: e.target.checked })
              }
            >
              Use transparent background
            </Checkbox>
          </Flex>
          <Tooltip placement="leftTop" title={<CheatSheet />}>
            <QuestionCircleFilled
              style={{
                cursor: "help",
                color: token.colorTextQuaternary,
              }}
            />
          </Tooltip>
        </Flex>
        <div
          style={{
            flex: 1,
            maxHeight: "100%",
            minHeight: 0,
            overflow: "auto",
            display: "flex",
            gap: 16,
          }}
        >
          <div style={{ flex: 1 }}>
            {isPreviewing ? (
              <Markdown className="markdown" skipHtml={true}>
                {working.markdown}
              </Markdown>
            ) : (
              <Input.TextArea
                style={{ height: "100%" }}
                value={working.markdown}
                onChange={(e) => {
                  updateWorking({ ...working, markdown: e.target.value });
                }}
              />
            )}
          </div>
        </div>
      </>
    </CardEditor>
  );
}

function CheatSheet() {
  return (
    <div>
      <h1># Heading 1</h1>
      <h2>## Heading 2</h2>
      <h3>### Heading 3</h3>
      <h4>#### Heading 4</h4>
      <h5>##### Heading 5</h5>
      <h6>###### Heading 6</h6>
      --- Divider <hr style={{ display: "inline-block", width: 100 }} />
      <br />
      **<em> Bold </em>**
      <br />*<em> Italics </em>*<br />
      {"`"}
      <code>monospace</code>
      {"`"}
      <br />
      <div style={{ marginLeft: 8, marginTop: 4 }}>
        - Bullet List Item
        <br />- Bullet List Item
      </div>
      <div style={{ marginLeft: 8, marginTop: 4 }}>
        1. Numbered List
        <br />
        2. Numbered List
        <br />
      </div>
      <blockquote>{"> blockquote"}</blockquote>
    </div>
  );
}
